/**
 * U0 · the toolbox — the one place a step is chosen, laid out as the mock-up lays it out.
 *
 *   What happens here?
 *   PREPARATION AND CLEARANCE   [RDY Readiness]  [CLR Clearance]
 *   MATERIAL                    [MCH Material charge]  [CAD Controlled addition]  [TRF Transfer]
 *   …
 *   Every entry is a step type from the closed vocabulary · a step the compiler cannot name
 *   is one it cannot check.
 *
 * Every entry is a step type from the closed vocabulary. There is no free-text entry, because
 * a typed step type is one the engine cannot evaluate. The same picker serves the toolbar's
 * "Add step" and every insertion point between rows — one place, one list.
 *
 * `source` is `[{ value, label, family, code?, hint? }]`. Code and hint are the vocabulary's
 * own words for the entry; the chip shows the code, the hint sits under the label.
 */
import React, { useMemo, useState } from "react";
import { Button, Dropdown, Empty, Input, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";

const { Text } = Typography;

export default function Inserter({ source, label = "Add step", icon, disabled, onPick }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = new Map();
    (source || []).forEach((item) => {
      if (q && !`${item.label} ${item.value} ${item.code || ""} ${item.hint || ""}`.toLowerCase().includes(q)) return;
      const family = item.family || "All";
      if (!out.has(family)) out.set(family, []);
      out.get(family).push(item);
    });
    return [...out.entries()];
  }, [source, query]);

  const pick = (item) => { setOpen(false); setQuery(""); onPick?.(item); };

  const menu = (
    <div className="u0-toolbox" onClick={(e) => e.stopPropagation()}>
      <div className="u0-toolhead">
        <span className="q">What happens here?</span>
        <Input size="small" allowClear placeholder="Find" value={query}
               onChange={(e) => setQuery(e.target.value)} style={{ width: 150 }} />
      </div>
      <div className="u0-toolbody">
        {groups.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing matches." />
        ) : (
          groups.map(([family, items]) => (
            <div key={family} className="u0-toolgrp">
              <div className="u0-eyebrow">{family}</div>
              <div className="u0-tools">
                {items.map((item) => (
                  <button key={item.value} type="button" className="u0-tool"
                          title={item.hint || item.label} onClick={() => pick(item)}>
                    {item.code ? <code>{item.code}</code> : null}
                    <span className="n">{item.label}</span>
                    {item.hint ? <span className="h">{item.hint}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="u0-toolfoot">
        <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))" }}>
          Every entry is a step type from the closed vocabulary · a step the compiler cannot
          name is one it cannot check.
        </Text>
      </div>
    </div>
  );

  // rootClassName: the popup is portaled to <body>, outside the .dz scope that declares the
  // tokens. Without it the toolbox's background resolved to nothing and the rows showed
  // through it.
  return (
    <Dropdown popupRender={() => menu} trigger={["click"]} disabled={disabled}
              open={open} onOpenChange={setOpen} destroyOnHidden rootClassName="dz">
      {icon ? (
        <button type="button" className="u0-insplus" disabled={disabled}
                aria-label="Insert a step here">+</button>
      ) : (
        <Button size="small" type="primary" icon={<PlusOutlined />} disabled={disabled}>
          {label}
        </Button>
      )}
    </Dropdown>
  );
}
